// ============================================================================
// ENGAGEMENT PLANNER TAB
// Setup → Sessions → Calendar views for configuring client engagements
// ============================================================================

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Flag, Sparkles, Search, Hammer, Package, ChevronDown, ChevronRight,
  Check, ExternalLink, Plus, GripVertical, X, ArrowRight,
  Download, Clock, Link2, BarChart3, MessageSquare, Zap, RefreshCw,
} from 'lucide-react';
import {
  EngagementConfig, EngagementTab, EngagementDuration,
  DEFAULT_PHASES,
  generateSessionTemplates, generateCalendarWeeks,
  SessionTemplate, Activity, SubItemType, CalendarEvent, EngagementConfigSaved,
  phaseTemplateToActivity, parseDurationToMinutes,
} from './engagementData';
import { browsePhaseTemplates, browseSessionTemplates, previewSessionTemplate } from '../../../services/templateService';
import { SessionTemplate as PlatformSessionTemplate } from '../../../types';
import { api } from '../../../services/api';

interface EngagementBuilderTabProps {
  projectId: string;
  token: string;
}

const PHASE_ICONS: Record<string, React.ElementType> = {
  flag: Flag,
  sparkles: Sparkles,
  search: Search,
  hammer: Hammer,
  package: Package,
};

const SUB_ITEM_TYPE_ICONS: Record<SubItemType, React.ElementType> = {
  link: ExternalLink,
  portal: Link2,
  campaign: BarChart3,
  session: MessageSquare,
  placeholder: Zap,
};

const SUB_ITEM_TYPE_LABELS: Record<SubItemType, string> = {
  link: 'External',
  portal: 'Portal',
  campaign: 'Campaign',
  session: 'Sessions',
  placeholder: 'Coming Soon',
};

const SUB_ITEM_TYPE_BADGE_CLASSES: Record<SubItemType, string> = {
  link: 'bg-blue-50 text-blue-600',
  portal: 'bg-primary/10 text-primary',
  campaign: 'bg-orange-50 text-orange-600',
  session: 'bg-indigo-50 text-indigo-600',
  placeholder: 'bg-slate-100 text-slate-500',
};

const DURATION_OPTIONS: EngagementDuration[] = ['1 week', '2 weeks', '3 weeks', '4 weeks', 'custom'];

// ============================================================================
// Pure config updaters (module-level, no closure capture)
// ============================================================================

function updTogglePhase(phaseId: string) {
  return (prev: EngagementConfig): EngagementConfig => ({
    ...prev,
    phases: prev.phases.map(p => p.id === phaseId ? { ...p, enabled: !p.enabled } : p),
  });
}

function updToggleSubItem(phaseId: string, subItemId: string) {
  return (prev: EngagementConfig): EngagementConfig => ({
    ...prev,
    phases: prev.phases.map(p =>
      p.id === phaseId
        ? { ...p, subItems: p.subItems.map(s => s.id === subItemId ? { ...s, enabled: !s.enabled } : s) }
        : p
    ),
  });
}

function updSessionCount(phaseId: string, subItemId: string, count: number) {
  return (prev: EngagementConfig): EngagementConfig => ({
    ...prev,
    phases: prev.phases.map(p =>
      p.id === phaseId
        ? { ...p, subItems: p.subItems.map(s => s.id === subItemId ? { ...s, sessionCount: Math.max(1, count) } : s) }
        : p
    ),
  });
}

function updAddAssetLink(phaseId: string, subItemId: string, label: string, url: string) {
  return (prev: EngagementConfig): EngagementConfig => ({
    ...prev,
    phases: prev.phases.map(p =>
      p.id === phaseId
        ? {
            ...p,
            subItems: p.subItems.map(s =>
              s.id === subItemId
                ? { ...s, assets: [...s.assets, { id: Date.now().toString(), label, url }] }
                : s
            ),
          }
        : p
    ),
  });
}

function updRemoveAssetLink(phaseId: string, subItemId: string, assetId: string) {
  return (prev: EngagementConfig): EngagementConfig => ({
    ...prev,
    phases: prev.phases.map(p =>
      p.id === phaseId
        ? {
            ...p,
            subItems: p.subItems.map(s =>
              s.id === subItemId
                ? { ...s, assets: s.assets.filter(a => a.id !== assetId) }
                : s
            ),
          }
        : p
    ),
  });
}

function updRemoveActivity(stIdx: number, activityId: string) {
  return (prev: SessionTemplate[]): SessionTemplate[] => prev.map((st, i) => {
    if (i !== stIdx) return st;
    const removed = st.activities.find(a => a.id === activityId);
    return {
      ...st,
      activities: st.activities.filter(a => a.id !== activityId),
      availableActivities: removed ? [...st.availableActivities, removed] : st.availableActivities,
    };
  });
}

function updAddActivity(stIdx: number, activityId: string) {
  return (prev: SessionTemplate[]): SessionTemplate[] => prev.map((st, i) => {
    if (i !== stIdx) return st;
    const added = st.availableActivities.find(a => a.id === activityId);
    return {
      ...st,
      availableActivities: st.availableActivities.filter(a => a.id !== activityId),
      activities: added ? [...st.activities, added] : st.activities,
    };
  });
}

function updMoveActivity(stIdx: number, activityId: string, direction: 'up' | 'down') {
  return (prev: SessionTemplate[]): SessionTemplate[] => prev.map((st, i) => {
    if (i !== stIdx) return st;
    const idx = st.activities.findIndex(a => a.id === activityId);
    if (idx < 0) return st;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= st.activities.length) return st;
    const newActivities = [...st.activities];
    [newActivities[idx], newActivities[newIdx]] = [newActivities[newIdx], newActivities[idx]];
    return { ...st, activities: newActivities };
  });
}

function updActivityDuration(stIdx: number, activityId: string, durationMin: number) {
  return (prev: SessionTemplate[]): SessionTemplate[] => prev.map((st, i) => {
    if (i !== stIdx) return st;
    return {
      ...st,
      activities: st.activities.map(a => a.id === activityId ? { ...a, durationMin } : a),
    };
  });
}

export const EngagementBuilderTab: React.FC<EngagementBuilderTabProps> = ({ projectId, token }) => {
  const [activeTab, setActiveTab] = useState<EngagementTab>('setup');
  const [config, setConfig] = useState<EngagementConfig>({
    clientName: '',
    startDate: '',
    duration: '2 weeks',
    track: 'standard',
    phases: JSON.parse(JSON.stringify(DEFAULT_PHASES)),
  });
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({ foundations: true });
  const [planGenerated, setPlanGenerated] = useState(false);

  // Session templates state (editable after plan generation)
  const [sessionTemplates, setSessionTemplates] = useState<SessionTemplate[]>([]);
  const [activeSessionIdx, setActiveSessionIdx] = useState(0);

  // Calendar events — mutable map of "weekIdx-dayIdx" → event (for drag-and-drop)
  const [calendarEvents, setCalendarEvents] = useState<Record<string, CalendarEvent>>({});

  // Phase Templates from the platform API
  const [phaseTemplateActivities, setPhaseTemplateActivities] = useState<Activity[]>([]);
  const [loadingPhaseTemplates, setLoadingPhaseTemplates] = useState(false);
  const [_phaseTemplatesLoaded, setPhaseTemplatesLoaded] = useState(false);

  // Platform Session Templates (for the template picker dropdown)
  const [platformSessionTemplates, setPlatformSessionTemplates] = useState<PlatformSessionTemplate[]>([]);

  // Save state
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const saveTimeoutRef = React.useRef<NodeJS.Timeout>(undefined);

  const fetchPhaseTemplates = useCallback(async () => {
    setLoadingPhaseTemplates(true);
    try {
      const { templates } = await browsePhaseTemplates({ limit: 100 });
      const activities = templates.map(pt => phaseTemplateToActivity(pt));
      setPhaseTemplateActivities(activities);
      setPhaseTemplatesLoaded(true);
    } catch (err) {
      console.warn('Could not fetch Phase Templates, using defaults:', err);
      setPhaseTemplatesLoaded(true);
    } finally {
      setLoadingPhaseTemplates(false);
    }
  }, []);

  const fetchSessionTemplates = useCallback(async () => {
    try {
      const { templates } = await browseSessionTemplates({ limit: 50 });
      setPlatformSessionTemplates(templates);
    } catch (err) {
      console.warn('Could not fetch Session Templates:', err);
    }
  }, []);

  // Save engagement config to backend (debounced)
  const saveEngagementConfig = useCallback(async (
    cfg: EngagementConfig, sessions: SessionTemplate[], events: Record<string, CalendarEvent>, generated: boolean
  ) => {
    try {
      setSaving(true);
      const payload: EngagementConfigSaved = {
        config: cfg,
        sessionTemplates: sessions,
        calendarEvents: events,
        planGenerated: generated,
      };
      await api.put(`/api/projects/${projectId}`, { engagementConfig: payload });
    } catch (err) {
      console.warn('Failed to save engagement config:', err);
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  // Auto-save on state changes (debounced 1.5s)
  useEffect(() => {
    if (!loaded) return; // Don't save until we've loaded
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveEngagementConfig(config, sessionTemplates, calendarEvents, planGenerated);
    }, 1500);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [config, sessionTemplates, calendarEvents, planGenerated, loaded, saveEngagementConfig]);

  // Load engagement config from backend on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await api.get(`/api/projects/${projectId}`);
        const project = response.data.project || response.data;
        if (project.engagementConfig) {
          const saved: EngagementConfigSaved = project.engagementConfig;
          setConfig(saved.config);
          setSessionTemplates(saved.sessionTemplates || []);
          setCalendarEvents(saved.calendarEvents || {});
          setPlanGenerated(saved.planGenerated || false);
        }
      } catch (err) {
        console.warn('Failed to load engagement config:', err);
      } finally {
        setLoaded(true);
      }
    };
    loadConfig();
  }, [projectId]);

  // Fetch phase and session templates on mount
  useEffect(() => {
    fetchPhaseTemplates();
    fetchSessionTemplates();
  }, [fetchPhaseTemplates, fetchSessionTemplates]);

  // Derived data
  const calendarWeeks = useMemo(() => generateCalendarWeeks(config), [config]);

  const handleGeneratePlan = () => {
    // Use Phase Templates if loaded, otherwise generateSessionTemplates uses hardcoded defaults
    const templates = generateSessionTemplates(
      config,
      phaseTemplateActivities.length > 0 ? phaseTemplateActivities : undefined,
    );
    setSessionTemplates(templates);
    setPlanGenerated(true);

    // Generate initial calendar event placements
    const weeks = generateCalendarWeeks(config);
    const events: Record<string, CalendarEvent> = {};
    weeks.forEach((week, wIdx) => {
      week.days.forEach((day, dIdx) => {
        if (day.event) {
          events[`${wIdx}-${dIdx}`] = day.event;
        }
      });
    });
    setCalendarEvents(events);

    setActiveTab('sessions');
  };

  const togglePhase = (phaseId: string) => {
    setConfig(updTogglePhase(phaseId));
  };

  const toggleSubItem = (phaseId: string, subItemId: string) => {
    setConfig(updToggleSubItem(phaseId, subItemId));
  };

  const updateSessionCount = (phaseId: string, subItemId: string, count: number) => {
    setConfig(updSessionCount(phaseId, subItemId, count));
  };

  const togglePhaseExpanded = (phaseId: string) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const addAssetLink = (phaseId: string, subItemId: string) => {
    const url = prompt('Enter asset URL:');
    if (!url) return;
    const label = prompt('Enter label (e.g. "2-Pager PDF"):') || 'Asset';
    setConfig(updAddAssetLink(phaseId, subItemId, label, url));
  };

  const removeAssetLink = (phaseId: string, subItemId: string, assetId: string) => {
    setConfig(updRemoveAssetLink(phaseId, subItemId, assetId));
  };

  // Session template editing
  const removeActivityFromSession = (stIdx: number, activityId: string) => {
    setSessionTemplates(updRemoveActivity(stIdx, activityId));
  };

  const addActivityToSession = (stIdx: number, activityId: string) => {
    setSessionTemplates(updAddActivity(stIdx, activityId));
  };

  const moveActivity = (stIdx: number, activityId: string, direction: 'up' | 'down') => {
    setSessionTemplates(updMoveActivity(stIdx, activityId, direction));
  };

  const toggleSessionDuration = (stIdx: number) => {
    setSessionTemplates(prev => prev.map((st, i) =>
      i === stIdx ? { ...st, isHalfDay: !st.isHalfDay } : st
    ));
  };

  const updateActivityDuration = (stIdx: number, activityId: string, durationMin: number) => {
    setSessionTemplates(updActivityDuration(stIdx, activityId, durationMin));
  };

  // Apply a platform Session Template to the currently active session
  const applySessionTemplate = async (stIdx: number, platformTemplateId: string) => {
    if (platformTemplateId === 'custom') {
      // Reset to defaults — keep current activities as-is (user can manually edit)
      return;
    }
    try {
      const { snapshot } = await previewSessionTemplate(platformTemplateId);
      const activities: Activity[] = snapshot.phases.map((phase, i) => ({
        id: `snap-${phase.phaseTemplateId}-${stIdx}-${i}`,
        name: phase.name,
        description: phase.description || phase.purpose || '',
        durationMin: parseDurationToMinutes(phase.duration),
        owner: 'Facilitator',
      }));
      setSessionTemplates(prev => prev.map((st, i) => {
        if (i !== stIdx) return st;
        return { ...st, activities, availableActivities: [] };
      }));
    } catch (err) {
      console.error('Failed to load session template:', err);
    }
  };

  // Calendar drag-and-drop
  const moveCalendarEvent = useCallback((fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    setCalendarEvents(prev => {
      const next = { ...prev };
      const event = next[fromKey];
      if (!event) return prev;
      // If target already has an event, swap
      if (next[toKey]) {
        next[fromKey] = next[toKey];
      } else {
        delete next[fromKey];
      }
      next[toKey] = event;
      return next;
    });
  }, []);

  // Tab navigation
  const tabs: { key: EngagementTab; label: string }[] = [
    { key: 'setup', label: 'Setup' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'calendar', label: 'Calendar' },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Tab bar */}
      <div className="flex border-b border-slate-200 gap-6">
        {tabs.map(tab => {
          const isTabDisabled = tab.key !== 'setup' && !planGenerated;
          let tabClass: string;
          if (activeTab === tab.key) tabClass = 'border-b-primary text-[#052831]';
          else if (isTabDisabled) tabClass = 'border-b-transparent text-slate-300 cursor-not-allowed';
          else tabClass = 'border-b-transparent text-slate-500 hover:text-slate-700';
          return (
            <button
              key={tab.key}
              onClick={() => {
                if (isTabDisabled) return;
                setActiveTab(tab.key);
              }}
              className={`pb-3 pt-1 px-1 text-sm font-bold border-b-[3px] transition-colors ${tabClass}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'setup' && (
        <SetupView
          config={config} setConfig={setConfig} expandedPhases={expandedPhases}
          togglePhaseExpanded={togglePhaseExpanded} togglePhase={togglePhase}
          toggleSubItem={toggleSubItem} updateSessionCount={updateSessionCount}
          addAssetLink={addAssetLink} removeAssetLink={removeAssetLink}
          onGenerate={handleGeneratePlan}
          phaseTemplateCount={phaseTemplateActivities.length}
          loadingPhaseTemplates={loadingPhaseTemplates}
        />
      )}
      {activeTab === 'sessions' && (
        <SessionsView
          sessions={sessionTemplates} activeIdx={activeSessionIdx} setActiveIdx={setActiveSessionIdx}
          onRemoveActivity={removeActivityFromSession} onAddActivity={addActivityToSession}
          onMoveActivity={moveActivity} onToggleDuration={toggleSessionDuration}
          onUpdateActivityDuration={updateActivityDuration}
          platformTemplates={platformSessionTemplates}
          onApplyTemplate={applySessionTemplate}
        />
      )}
      {activeTab === 'calendar' && (
        <CalendarView
          config={config}
          weeks={calendarWeeks}
          calendarEvents={calendarEvents}
          onMoveEvent={moveCalendarEvent}
          saving={saving}
        />
      )}
    </div>
  );
};

// ============================================================================
// ASSET LINK LIST (extracted to avoid deep nesting in SetupView)
// ============================================================================

interface AssetLinkListProps {
  phaseId: string;
  subItemId: string;
  assets: { id: string; label: string; url: string }[];
  onAdd: (phaseId: string, subItemId: string) => void;
  onRemove: (phaseId: string, subItemId: string, assetId: string) => void;
}

const AssetLinkList: React.FC<AssetLinkListProps> = ({ phaseId, subItemId, assets, onAdd, onRemove }) => (
  <div className="ml-8 flex flex-col gap-1.5">
    {assets.map(asset => (
      <div key={asset.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200 shadow-sm">
        <ExternalLink size={14} className="text-primary shrink-0" />
        <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:underline truncate flex-1">
          {asset.label}
        </a>
        <button onClick={() => onRemove(phaseId, subItemId, asset.id)} className="text-slate-400 hover:text-red-500">
          <X size={14} />
        </button>
      </div>
    ))}
    <button
      onClick={() => onAdd(phaseId, subItemId)}
      className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-dark font-medium mt-0.5"
    >
      <Plus size={12} /> Add asset link
    </button>
  </div>
);

// ============================================================================
// SETUP VIEW
// ============================================================================

interface SetupViewProps {
  config: EngagementConfig;
  setConfig: React.Dispatch<React.SetStateAction<EngagementConfig>>;
  expandedPhases: Record<string, boolean>;
  togglePhaseExpanded: (id: string) => void;
  togglePhase: (id: string) => void;
  toggleSubItem: (phaseId: string, subItemId: string) => void;
  updateSessionCount: (phaseId: string, subItemId: string, count: number) => void;
  addAssetLink: (phaseId: string, subItemId: string) => void;
  removeAssetLink: (phaseId: string, subItemId: string, assetId: string) => void;
  onGenerate: () => void;
  phaseTemplateCount: number;
  loadingPhaseTemplates: boolean;
}

const SetupView: React.FC<SetupViewProps> = ({
  config, setConfig, expandedPhases, togglePhaseExpanded, togglePhase, toggleSubItem,
  updateSessionCount, addAssetLink, removeAssetLink, onGenerate, phaseTemplateCount, loadingPhaseTemplates,
}) => {
  let phaseTemplateStatus: React.ReactNode;
  if (loadingPhaseTemplates) {
    phaseTemplateStatus = (
      <span className="flex items-center justify-center gap-1"><RefreshCw size={10} className="animate-spin" /> Loading Phase Templates...</span>
    );
  } else if (phaseTemplateCount > 0) {
    phaseTemplateStatus = <span>{phaseTemplateCount} Phase Template(s) loaded from library</span>;
  } else {
    phaseTemplateStatus = <span>Using default session activities (no Phase Templates found)</span>;
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Engagement Start Date & Duration */}
      <div className="bg-slate-50 rounded-xl p-6 shadow-sm border border-slate-100">
        <div className="flex flex-wrap items-end gap-6">
          <label className="flex flex-col min-w-[200px]">
            <p className="text-sm font-medium text-slate-700 pb-2">Engagement Start Date</p>
            <input
              type="date"
              className="form-input rounded-xl border-slate-200 bg-white h-12 px-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              value={config.startDate}
              onChange={e => setConfig(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </label>
          <div className="flex flex-col flex-1 min-w-[300px]">
            <p className="text-sm font-medium text-slate-700 pb-2">Duration</p>
            <div className="flex h-12 items-center rounded-xl bg-slate-100 p-1">
              {DURATION_OPTIONS.map(d => (
                <button
                  key={d}
                  onClick={() => setConfig(prev => ({ ...prev, duration: d }))}
                  className={`flex-1 h-full rounded-lg text-sm font-bold transition-all ${
                    config.duration === d
                      ? 'bg-[#052831] text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Phases */}
      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-bold text-[#052831]">Co-Creation Phases</h3>
        <p className="text-sm text-slate-500">Select which phases to include and configure their sub-items.</p>
        <div className="flex flex-col gap-3">
          {config.phases.map(phase => {
            const PhaseIcon = PHASE_ICONS[phase.icon] || Flag;
            const isExpanded = expandedPhases[phase.id];
            const enabledCount = phase.subItems.filter(s => s.enabled).length;
            return (
              <div
                key={phase.id}
                className={`rounded-xl border overflow-hidden shadow-sm transition-all ${
                  phase.enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'
                }`}
              >
                {/* Phase header */}
                <div className={`flex items-center p-4 border-l-4 ${phase.color}`}>
                  <div className="flex-1 flex items-center gap-3">
                    <button
                      onClick={() => togglePhase(phase.id)}
                      className={`size-8 rounded-full flex items-center justify-center transition-colors ${
                        phase.enabled ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-400'
                      }`}
                    >
                      {phase.enabled ? <Check size={16} /> : <PhaseIcon size={16} />}
                    </button>
                    <h4 className="font-bold text-slate-900">{phase.name}</h4>
                    {phase.enabled && (
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-xs font-medium text-slate-500">
                        {enabledCount}/{phase.subItems.length} items
                      </span>
                    )}
                  </div>
                  <button onClick={() => togglePhaseExpanded(phase.id)} className="text-slate-400 hover:text-slate-600">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                </div>

                {/* Sub-items */}
                {isExpanded && phase.enabled && (
                  <div className="p-4 pt-0 pl-16 border-t border-slate-100 bg-slate-50/50">
                    <div className="flex flex-col gap-4 mt-4">
                      {phase.subItems.map(sub => {
                        const TypeIcon = SUB_ITEM_TYPE_ICONS[sub.type];
                        return (
                          <div key={sub.id} className="flex flex-col gap-2">
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={sub.enabled}
                                onChange={() => toggleSubItem(phase.id, sub.id)}
                                className="form-checkbox rounded text-primary focus:ring-primary border-slate-300 size-5 mt-0.5"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {sub.link ? (
                                    <a href={sub.link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline">{sub.label}</a>
                                  ) : (
                                    <span className="text-sm font-medium text-slate-700">{sub.label}</span>
                                  )}
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${SUB_ITEM_TYPE_BADGE_CLASSES[sub.type]}`}>
                                    <TypeIcon size={10} />
                                    {SUB_ITEM_TYPE_LABELS[sub.type]}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">{sub.description}</p>

                                {/* Session count for session-type items */}
                                {sub.enabled && sub.type === 'session' && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs text-slate-500">Sessions:</span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => updateSessionCount(phase.id, sub.id, (sub.sessionCount || 1) - 1)}
                                        className="size-6 rounded bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 flex items-center justify-center"
                                      >
                                        -
                                      </button>
                                      <span className="text-sm font-bold text-slate-700 w-6 text-center">{sub.sessionCount || 1}</span>
                                      <button
                                        onClick={() => updateSessionCount(phase.id, sub.id, (sub.sessionCount || 1) + 1)}
                                        className="size-6 rounded bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 flex items-center justify-center"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Asset links */}
                            {sub.enabled && (
                              <AssetLinkList
                                phaseId={phase.id}
                                subItemId={sub.id}
                                assets={sub.assets}
                                onAdd={addAssetLink}
                                onRemove={removeAssetLink}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Generate button */}
      <div className="flex flex-col gap-2">
        <button
          onClick={onGenerate}
          disabled={!config.startDate}
          className="w-full flex items-center justify-center gap-2 rounded-xl h-14 bg-primary text-white text-lg font-bold shadow-md hover:bg-primary/90 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Generate Engagement Plan <ArrowRight size={20} />
        </button>
        <p className="text-xs text-slate-400 text-center">
          {phaseTemplateStatus}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// SESSIONS VIEW (Discovery session agenda builder)
// ============================================================================

interface SessionsViewProps {
  sessions: SessionTemplate[];
  activeIdx: number;
  setActiveIdx: (idx: number) => void;
  onRemoveActivity: (stIdx: number, activityId: string) => void;
  onAddActivity: (stIdx: number, activityId: string) => void;
  onMoveActivity: (stIdx: number, activityId: string, dir: 'up' | 'down') => void;
  onToggleDuration: (stIdx: number) => void;
  onUpdateActivityDuration: (stIdx: number, activityId: string, durationMin: number) => void;
  platformTemplates: PlatformSessionTemplate[];
  onApplyTemplate: (stIdx: number, templateId: string) => void;
}

const SessionsView: React.FC<SessionsViewProps> = ({
  sessions, activeIdx, setActiveIdx, onRemoveActivity, onAddActivity, onMoveActivity, onToggleDuration, onUpdateActivityDuration,
  platformTemplates, onApplyTemplate,
}) => {
  if (sessions.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <p className="text-lg font-medium">No discovery sessions configured.</p>
        <p className="text-sm mt-1">Enable Co-Creation Discovery in the Setup tab and generate a plan first.</p>
      </div>
    );
  }

  const st = sessions[activeIdx];
  if (!st) return null;

  const totalMin = st.activities.reduce((sum, a) => sum + a.durationMin, 0);
  const maxMin = st.isHalfDay ? 240 : 480;
  let runningTime = 9 * 60; // 09:00

  return (
    <div className="flex flex-col gap-6">
      {/* Session selector pills */}
      <div className="flex flex-wrap gap-2">
        {sessions.map((s, idx) => (
          <button
            key={s.id}
            onClick={() => setActiveIdx(idx)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              idx === activeIdx
                ? 'text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            style={idx === activeIdx ? { backgroundColor: s.colorHex } : undefined}
          >
            {s.name}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="flex gap-6">
        {/* Left: Agenda */}
        <div className="flex-1 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-[#052831]">{st.name} — Agenda</h3>
            <div className="flex rounded-lg overflow-hidden border border-slate-200">
              <button
                onClick={() => onToggleDuration(activeIdx)}
                className={`px-4 py-2 text-sm font-bold transition-colors ${st.isHalfDay ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Half Day
              </button>
              <button
                onClick={() => onToggleDuration(activeIdx)}
                className={`px-4 py-2 text-sm font-bold transition-colors ${!st.isHalfDay ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Full Day
              </button>
            </div>
          </div>

          {/* Duration bar */}
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-600 font-medium">{totalMin} / {maxMin} min</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.min(100, (totalMin / maxMin) * 100)}%` }}
              />
            </div>
            <span className="text-slate-400">{Math.max(0, maxMin - totalMin)} min left</span>
          </div>

          {/* Activity list */}
          <div className="flex flex-col gap-3">
            {st.activities.map((activity, idx) => {
              const startTime = `${String(Math.floor(runningTime / 60)).padStart(2, '0')}:${String(runningTime % 60).padStart(2, '0')}`;
              runningTime += activity.durationMin;
              return (
                <div key={activity.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <GripVertical size={18} className="text-slate-300 mt-1 cursor-grab shrink-0" />
                  <span className="text-xs text-slate-400 font-mono mt-1 w-10 shrink-0">{startTime}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900">{activity.name}</span>
                      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10">
                        <input
                          type="number"
                          min={5}
                          max={240}
                          step={5}
                          value={activity.durationMin}
                          onChange={e => onUpdateActivityDuration(activeIdx, activity.id, Math.max(5, parseInt(e.target.value) || 5))}
                          className="w-12 bg-transparent text-primary text-xs font-bold text-center outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-primary text-xs font-bold">min</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{activity.description}</p>
                  </div>
                  <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-md whitespace-nowrap shrink-0">
                    {activity.owner}
                  </span>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button onClick={() => onMoveActivity(activeIdx, activity.id, 'up')} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ChevronDown size={14} className="rotate-180" />
                    </button>
                    <button onClick={() => onMoveActivity(activeIdx, activity.id, 'down')} disabled={idx === st.activities.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <button onClick={() => onRemoveActivity(activeIdx, activity.id)} className="text-slate-400 hover:text-red-500 shrink-0">
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Template picker + Available activities */}
        <div className="w-72 shrink-0 flex flex-col gap-4">
          {/* Session Template picker */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <h4 className="font-bold text-sm text-slate-700 mb-2">Session Template</h4>
            <p className="text-xs text-slate-400 mb-3">Apply a template to populate phases.</p>
            <select
              className="form-select w-full rounded-lg border-slate-200 text-sm py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
              defaultValue=""
              onChange={e => {
                if (e.target.value) onApplyTemplate(activeIdx, e.target.value);
                e.target.value = '';
              }}
            >
              <option value="" disabled>Select a template...</option>
              {platformTemplates.map(t => (
                <option key={t.sessionTemplateId} value={t.sessionTemplateId}>
                  {t.name}
                </option>
              ))}
              <option value="custom">Custom (keep current)</option>
            </select>
            {platformTemplates.length === 0 && (
              <p className="text-[10px] text-slate-400 mt-2">No session templates found. Create templates in the Session Template Catalog.</p>
            )}
          </div>

          {/* Available Activities */}
          <div className="bg-amber-50/50 rounded-xl border border-amber-100 p-4">
            <h4 className="font-bold text-sm text-slate-700 mb-3">Available Activities</h4>
            <div className="flex flex-col gap-2">
              {st.availableActivities.map(activity => (
                <button
                  key={activity.id}
                  onClick={() => onAddActivity(activeIdx, activity.id)}
                  className="text-left p-3 rounded-lg bg-white border-l-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  style={{ borderLeftColor: st.colorHex }}
                >
                  <span className="text-sm font-bold text-slate-800">{activity.name}</span>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Clock size={10} className="text-slate-400" />
                    <span className="text-[10px] text-slate-400 font-medium">{activity.durationMin} min</span>
                  </div>
                </button>
              ))}
              {st.availableActivities.length === 0 && (
                <p className="text-xs text-slate-400 italic">All activities are in the agenda.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CALENDAR VIEW (with drag-and-drop)
// ============================================================================

interface CalendarViewProps {
  config: EngagementConfig;
  weeks: ReturnType<typeof generateCalendarWeeks>;
  calendarEvents: Record<string, CalendarEvent>;
  onMoveEvent: (fromKey: string, toKey: string) => void;
  saving: boolean;
}

const CalendarView: React.FC<CalendarViewProps> = ({ config, weeks, calendarEvents, onMoveEvent, saving }) => {
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, key: string) => {
    e.dataTransfer.setData('text/plain', key);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  };

  const handleDragLeave = () => {
    setDragOverKey(null);
  };

  const handleDrop = (e: React.DragEvent, toKey: string) => {
    e.preventDefault();
    setDragOverKey(null);
    const fromKey = e.dataTransfer.getData('text/plain');
    if (fromKey) onMoveEvent(fromKey, toKey);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold text-[#052831]">Engagement Calendar</h3>
          {saving && (
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <RefreshCw size={10} className="animate-spin" /> Saving...
            </span>
          )}
        </div>
        <button className="flex items-center gap-2 px-4 h-10 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors shadow-sm">
          <Download size={16} /> Export All (.ics)
        </button>
      </div>

      {weeks.map((week, wIdx) => (
        <div key={week.label} className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-bold text-[#052831]">{week.label}</h4>
            <span className="text-xs text-slate-400 font-medium">{week.dateRange}</span>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {week.days.map((day, dIdx) => {
              const cellKey = `${wIdx}-${dIdx}`;
              const event = calendarEvents[cellKey];
              const isDragOver = dragOverKey === cellKey;
              return (
                <div
                  key={day.label}
                  className={`bg-white rounded-xl border min-h-[120px] p-3 flex flex-col transition-all ${
                    isDragOver ? 'border-primary border-2 bg-primary/5 scale-[1.02]' : 'border-slate-200'
                  }`}
                  onDragOver={e => handleDragOver(e, cellKey)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, cellKey)}
                >
                  <span className="text-xs text-slate-500 font-medium mb-2">{day.label}</span>
                  {event ? (
                    <div
                      draggable
                      onDragStart={e => handleDragStart(e, cellKey)}
                      className="flex-1 rounded-lg p-3 flex flex-col justify-between text-white shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                      style={{ backgroundColor: event.color }}
                    >
                      <span className="text-sm font-bold leading-tight">{event.name}</span>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-medium opacity-90">
                          {event.isHalfDay ? '½ day' : 'Full day'}
                        </span>
                        <button className="bg-white/20 rounded p-1 hover:bg-white/30 transition-colors">
                          <Download size={12} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={`flex-1 flex items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                      isDragOver ? 'border-primary bg-primary/5' : 'border-transparent'
                    }`}>
                      <span className="text-xs text-slate-300 italic">
                        {isDragOver ? 'Drop here' : ''}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EngagementBuilderTab;
